import { getDayGroupLabel, groupByDay } from '../../../Utils/dayGroups';
import { formatTimeHMAR } from '../../../Utils/dateTimeFormat';

const ts = (value) => {
  const t = new Date(value || 0).getTime();
  return Number.isNaN(t) ? 0 : t;
};

/**
 * Filas de `getlothistory` → una por lote (la visita más reciente).
 * La API puede devolver una fila por visita; el panel muestra el lote una vez.
 * Campos de la card: `lot_id`, `lot_name`, `province_name`, `department_name`,
 * `visited_at` (= `viewed_at`).
 *
 * @param {object[]} rows
 * @returns {{ lot_id: string, lot_name: string, province_name: string,
 *   department_name: string, visited_at: string }[]}
 */
export function normalizeHistoryRows(rows) {
  const byLot = new Map();
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    const id = r?.lot_id != null ? String(r.lot_id).trim() : '';
    if (!id) return;
    const row = {
      lot_id: id,
      lot_name: r?.lot_name || '',
      province_name: r?.province_name || '',
      department_name: r?.department_name || '',
      visited_at: r?.viewed_at || '',
    };
    const prev = byLot.get(id);
    if (!prev || ts(row.visited_at) > ts(prev.visited_at)) byLot.set(id, row);
  });
  return Array.from(byLot.values());
}

/**
 * Búsqueda local por lo que se ve en la card: número catastral, departamento
 * y provincia. No busca por el id interno del lote.
 */
export function filterHistoryRows(rows, search) {
  const list = Array.isArray(rows) ? rows : [];
  const term = String(search || '').trim().toLocaleLowerCase('es');
  if (!term) return list;
  return list.filter((row) => [row.lot_name, row.department_name, row.province_name]
    .some((value) => String(value || '').toLocaleLowerCase('es').includes(term)));
}

export const HISTORY_SORT_RECENT = 'recent';
export const HISTORY_SORT_OLDEST = 'oldest';
export const DEFAULT_HISTORY_SORT = HISTORY_SORT_RECENT;

/** Opciones de «Ordenar por:» (`ListSortMenu`, estilo Registros). */
export const HISTORY_SORT_OPTIONS = [
  { value: HISTORY_SORT_RECENT, label: 'Más recientes' },
  { value: HISTORY_SORT_OLDEST, label: 'Más antiguos' },
];

/**
 * Grupos de día («Hoy», «Ayer», «Esta semana», «Semana pasada» o el mes).
 * Más recientes: el grupo y la visita más nuevos arriba. Más antiguos: todo
 * al revés (grupos y cards), así el orden y los grupos no se contradicen.
 */
export function groupHistoryRows(rows, now = new Date(), sortBy = DEFAULT_HISTORY_SORT) {
  const groups = groupByDay(rows, (row) => row?.visited_at, now);
  if (sortBy !== HISTORY_SORT_OLDEST) return groups;
  return groups
    .map((group) => ({ ...group, entries: group.entries.slice().reverse() }))
    .reverse();
}

/** «1 lote visto» / «12 lotes vistos»; con búsqueda «3 de 12 lotes vistos». */
export function formatHistoryCountsLabel(shown, total) {
  const noun = total === 1 ? 'lote visto' : 'lotes vistos';
  return shown === total ? `${total} ${noun}` : `${shown} de ${total} ${noun}`;
}

/**
 * Hora de la visita en la card. El grupo ya dice el día en «Hoy» / «Ayer»:
 * «Visto a las 14:32». En el resto suma día/mes: «Visto el 15/10 a las 14:32».
 */
export function formatHistoryVisitedLabel(value, groupLabel = null) {
  const hm = formatTimeHMAR(value);
  if (!hm) return '';
  const label = groupLabel || getDayGroupLabel(value);
  if (label === 'Hoy' || label === 'Ayer') return `Visto a las ${hm}`;
  const dt = value instanceof Date ? value : new Date(value);
  const day = dt.toLocaleDateString('es-AR', { day: 'numeric', month: 'numeric' });
  return `Visto el ${day} a las ${hm}`;
}
